import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {commentEditShowModal} from "features/modals/comment-edit/selectors";
import {withToastManager} from "react-toast-notifications";
import {favoriteEventStart} from "store/reducer/event/favorite/reducer";
import {startSingleEvent} from "features/pages/single-event/reducer";
import {singleEventData, singleEventErrorSelector, singleEventLoading} from "features/pages/single-event/selectors";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import {LoadingLogoPortal} from "features/components/loading-logo";
import Text from "features/components/text";
import {__} from "services/i18n";
import {SpeakerBox} from "features/components/speakers";
import {CategoryTagBox} from "features/components/category-tag-box";
import {Icon} from "features/components/icon";
import {ISO_8601} from "moment";
import moment from "moment";
import Title from "features/components/title";
import {LikeBox} from "features/components/like-box";
import CommentField from "features/components/comment-field";
import CommentBox from "features/components/comment-box";
import VideoCard from "features/components/video-card";
import Button from "features/components/button";
import {likeEventStart} from "store/reducer/event/like/reducer";
import {likeEventErrorSelector} from "store/reducer/event/like/selectors";
import SuggestedResource from "features/components/suggested";
import {useMediaQuery} from "services/utils/utils";
import {MOBILE_MEDIA_QUERY_THRESHOLD} from "constants.js";
import {ReportAProblemButton} from "features/components/problem-button";
import {MaterialBox} from "features/components/material-box";

/* istanbul ignore next */
const mapStateToProps = state => ({
    event: singleEventData(state),
    error: singleEventErrorSelector(state),
    loading: singleEventLoading(state),
    like_error: likeEventErrorSelector(state),
    should_show_edit_modal: commentEditShowModal(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    startSingleEvent,
    favoriteEventStart,
    likeEventStart
};


export const SingleEventPage = (props) => {
    const {event, error, loading, like_error, startSingleEvent, favoriteEventStart, likeEventStart, should_show_edit_modal} = props;

    React.useEffect(() => {
        startSingleEvent({id: props.match.params.id})
    }, [props.match.params.id, startSingleEvent]);

    React.useEffect(() => {
        if(!error.inError) return;
        props.toastManager.add(__(error.error), { appearance: 'error', id: 'error-load-single-video-toast'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    React.useEffect(() => {
        if(!like_error.inError) return;
        props.toastManager.add(__(like_error.error), { appearance: 'error', id: 'error-like-video-toast'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [like_error]);

    const [favorite, setFavorite] = useState((event || {}).favorite);

    React.useEffect(() => {
        if(!event) return;
        setFavorite(event.favorite);
    }, [event]);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);

    const toggleFavorite = useCallback((ev) => {
        setFavorite(!favorite);
        favoriteEventStart({id: event.id, favorite: !event.favorite});
        ev.preventDefault();
        ev.stopPropagation();
    }, [favorite, event, favoriteEventStart]);


    const isPast = useMemo(() => {
        if(!event) return false;
        return moment(event.end_date, ISO_8601) < moment();
    }, [event]);
    const isFull = useMemo(() => {
        if(!event) return false;
        return event.tickets_available === 0 && !event.unlimited_tickets;
    }, [event]);

    const breadcrumbTitle = useMemo(() => {
        if(isMobile) return "single-video.breadcrumb";
        return event ? event.title : "single-video.breadcrumb"
    }, [event, isMobile]);

    const renderEventData = useCallback(() => {
        const start = moment(event.start_date, ISO_8601);
        const end = moment(event.end_date, ISO_8601);
        const isSameDay = start.isSame(end, 'day');

        const day = isSameDay ? start.format("D MMMM") : `${start.format("D MMMM")} - ${end.format("D MMMM")}`;
        const time = `${start.format("HH:mm")} - ${end.format("HH:mm")}`
        return <div className="row dates">
            <div className="row">
                <Icon>calendar</Icon>
                <Text size={18}>{day}</Text>
            </div>
            <div className="row">
                <Icon>time</Icon>
                <Text size={18}>{time}</Text>
            </div>
            <div className="row">
                <Icon>world</Icon>
                <Text size={18}>{event.category}</Text>
            </div>
        </div>
    }, [event]);

    const toggleEventLike = useCallback(() => {
        likeEventStart({id: event.id, like: !event.like});
    }, [likeEventStart, event]);


    const renderVideos = useCallback(() => {
        if(!event || event.videos.length === 0) return null;
        return <>
            <Title size={28}>{__("single-event.past-title")}</Title>
            <div className="past-videos">
                {event.videos.map(i => <VideoCard key={i.id} video={i} />)}
            </div>
        </>
    }, [event]);

    const goToEventPage = useCallback(() => {
        window.open(event.link.link, "_blank");
    }, [event]);

    const renderSignUp = useCallback(() => {
        return <div className="event-signup">
            <div className="location">
                <div>
                    <Text size={14}>{__("single-event.location")}</Text>
                    <Title size={18}>{event.place}</Title>
                </div>
                {!event.unlimited_tickets && <div>
                    <Text size={14}>{__("single-event.tickets-available")}</Text>
                    <Title size={18}>{event.tickets_available}</Title>
                </div>}
            </div>
            {(!!event.link && !!event.link.label) && <div className="signup">
                <Title size={24}>{__("single-event.title-signup")}</Title>
                <Button title={event.link.label} onClick={goToEventPage} icon={"exit"} type={"outlined"}/>
            </div>}
        </div>
    }, [event, goToEventPage]);

    const renderFavoriteSpeakers = useCallback(() => {
        return <>
            <div className="row favorite">
                <Text size={16}>{__(`common.event-type.${event.type}`)}</Text>
                <div className="favorite-btn" onClick={toggleFavorite}>
                    <Icon>{favorite ? "heart-fill": "heart"}</Icon>
                </div>
            </div>
            <CategoryTagBox categories={event.categories} tags={event.tags} />
            {event.speakers && <SpeakerBox speakers={event.speakers}/>}
        </>
    }, [event, favorite, toggleFavorite]);
    
    const renderContent = useCallback(() => {
        if (loading) {
            return <LoadingLogoPortal/>;
        }
        if (!event) {
            return <Text size={16} align={"center"}>{__("single-event.not-found")}</Text>
        }
        return <><div className="sidebar-wrapper">
            <div className="content">
                <div className="event-data">
                    <img src={event.image} alt={"event banner"}/>
                    <div className="backdrop" />
                    <div className="flags">
                        {!isPast && isFull && <div className="full"><Title size={14}>{__("single-event.full")}</Title></div>}
                        {isPast && <div className="past"><Title size={14}>{__("single-event.past")}</Title></div>}
                    </div>
                    <div className="flags right">
                        {event.requires_payment && <div className="paid">
                            <Icon>lock</Icon>
                            <Title size={14}>€ {event.cost}</Title>
                        </div>}
                    </div>
                    <div className="data">
                        <Title size={34}>{event.title}</Title>
                        {renderEventData()}
                    </div>
                </div>
                {isPast ? renderVideos() : renderSignUp()}
                {isMobile && renderFavoriteSpeakers()}
                <div className={"like-comment"}>
                    <LikeBox onLike={toggleEventLike} total={event.likes.total} like={event.like} avatars={event.likes.avatars} />
                    {!should_show_edit_modal && <CommentField type={"event"} resource_id={event.id} />}
                </div>
                <CommentBox type={"event"} resource_id={event.id} />
            </div>
            <div className="sidebar">
                {!isMobile && renderFavoriteSpeakers()}
                <span className="separator" />
                <Title size={24}>{__("single-event.description.title")}</Title>
                <Text size={16}>{event.description}</Text>
                {(event.material && event.material.length > 0) && <MaterialBox data={event.material} locked={false} />}
                <ReportAProblemButton />
            </div>
        </div>
        {<SuggestedResource resource_id={event.id} type={"event"} />}
        </>
    }, [event, isFull, isPast, loading, renderEventData, renderSignUp, renderVideos, should_show_edit_modal, toggleEventLike, isMobile, renderFavoriteSpeakers]);

    return <LoggedInLayout
        type={"light"}
        className={"single-event-page"}
        breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "events.breadcrumb", link: "/events"}, {title: breadcrumbTitle}]}>
        {renderContent()}
    </LoggedInLayout>
};

export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(SingleEventPage));
